import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import RelationshipPicker from "@/components/common/RelationshipPicker";
import { FIELD_DEFS, GROUPS } from "./fields";
import { TESTIDS } from "@/lib/testIds";

/**
 * FieldEditor + FieldGroups — the render primitives shared by
 * ManualEntry, ExtractionReview, and BookingMetadata. Every group is
 * collapsible so the review surface stays scannable even as new
 * booking-metadata fields land in Sprint 3.
 */
export function FieldGroups({ fields, editField, openGroups, setOpenGroups, groupFilter }) {
  const groups = groupFilter ? GROUPS.filter((g) => groupFilter.includes(g.key)) : GROUPS;
  return (
    <div className="space-y-2" data-testid={TESTIDS.createCruiseFieldGroups}>
      {groups.map((g) => {
        const groupFields = FIELD_DEFS.filter((f) => f.group === g.key);
        if (!groupFields.length) return null;
        const open = openGroups[g.key];
        return (
          <div key={g.key} className="border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setOpenGroups((s) => ({ ...s, [g.key]: !s[g.key] }))}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 text-left"
              data-testid={TESTIDS.createCruiseFieldGroup(g.key)}
              type="button"
            >
              <div className="flex items-center gap-2">
                {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                <span className="font-display text-[15px] font-bold text-slate-900">{g.label}</span>
              </div>
              <span className="text-[11px] text-slate-500">{groupFields.length} fields</span>
            </button>
            {open && (
              <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {groupFields.map((f) => (
                  <FieldEditor
                    key={f.key}
                    field={f}
                    value={fields[f.key]}
                    onChange={(v) => editField(f.key, v)}
                    fields={fields}
                    editField={editField}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FieldEditor({ field, value, onChange, fields, editField }) {
  const testid = TESTIDS.createCruiseField(field.key);
  const common = "w-full text-[13px] px-2 py-1.5 rounded border border-slate-200 focus:border-amber-400 focus:outline-none bg-white";
  const label = (
    <label className="text-[10.5px] font-black tracking-[0.14em] uppercase text-slate-500 block mb-1">
      {field.label}{field.required && <span className="text-red-500"> *</span>}
    </label>
  );

  if (field.type === "money") {
    return (
      <div>
        {label}
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[13px]">$</span>
          <input
            type="number"
            step="0.01"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            placeholder="0.00"
            className={`${common} pl-6`}
            data-testid={testid}
          />
        </div>
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div>
        {label}
        <input
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          className={common}
          data-testid={testid}
        />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        {label}
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          className={common}
          data-testid={testid}
        >
          <option value="">Choose…</option>
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }
  if (field.type === "list") {
    if (field.key === "travelers") {
      return (
        <div className="md:col-span-2">
          {label}
          <RelationshipPicker
            entityType="traveler"
            value={{ ids: fields?.traveler_ids || [] }}
            legacyNames={Array.isArray(value) ? value : []}
            multi
            allowCreate
            allowEdit
            allowView
            onChange={(sel) => {
              onChange(sel.names);
              if (editField) editField("traveler_ids", sel.ids);
            }}
            testId={testid}
          />
        </div>
      );
    }
    return (
      <div className="md:col-span-2">
        {label}
        <ListInput value={value} onChange={onChange} testid={testid} className={common} />
      </div>
    );
  }
  return (
    <div>
      {label}
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={field.placeholder || "—"}
        className={common}
        data-testid={testid}
      />
    </div>
  );
}

/** Buffered comma-separated list input. Commits on blur / Enter. */
function ListInput({ value, onChange, testid, className }) {
  const initial = Array.isArray(value) ? value.join(", ") : (typeof value === "string" ? value : "");
  const [buffer, setBuffer] = React.useState(initial);
  React.useEffect(() => {
    const incoming = Array.isArray(value) ? value.join(", ") : (typeof value === "string" ? value : "");
    setBuffer((prev) => {
      const parsed = _parseList(prev).join(", ");
      return incoming !== parsed ? incoming : prev;
    });
  }, [value]);
  const commit = () => onChange(_parseList(buffer));
  return (
    <input
      type="text"
      value={buffer}
      onChange={(e) => setBuffer(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
      placeholder="Comma-separated"
      className={className}
      data-testid={testid}
    />
  );
}

function _parseList(str) {
  if (!str) return [];
  return String(str).split(",").map((s) => s.trim()).filter(Boolean);
}
